import { Fragment, useEffect, useRef, useState } from 'react';
import { fx } from '@/platform/feedback';
import styles from './RoundStartSequence.module.css';

type Phase = 3 | 2 | 1 | 'reveal';

const COLORS = ['var(--color-l1)', 'var(--color-l2)', 'var(--color-l3)'] as const;
const GLOWS  = ['#ffb45488', '#6ad1c688', '#c98aff88'] as const;
const COUNT_COLOR: Record<number, string> = { 3: COLORS[0], 2: COLORS[1], 1: COLORS[2] };
const COUNT_GLOW:  Record<number, string> = { 3: GLOWS[0],  2: GLOWS[1],  1: GLOWS[2]  };

export interface RoundStartSequenceProps {
  letters: [string, string, string];
  roundLabel?: string;
  onDone: () => void;
  reducedMotion?: boolean;
}

export function RoundStartSequence({
  letters,
  roundLabel,
  onDone,
  reducedMotion,
}: RoundStartSequenceProps) {
  const [phase, setPhase] = useState<Phase>(3);
  const [lifted, setLifted] = useState(false);
  const doneRef = useRef(false);

  const finish = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    onDone();
  };

  useEffect(() => {
    if (reducedMotion) {
      finish();
      return;
    }
    fx.count();
    const timers = [
      setTimeout(() => { setPhase(2); fx.count(); },        700),
      setTimeout(() => { setPhase(1); fx.count(); },        1400),
      setTimeout(() => { setPhase('reveal'); fx.go(); },    2100),
      setTimeout(() => setLifted(true),                     2900),
      setTimeout(finish,                                    3500),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const isCount = phase !== 'reveal';
  const glowBg = isCount
    ? `radial-gradient(60% 40% at 50% 42%, ${COUNT_GLOW[phase as number] ?? '#4f8cff22'}, transparent 70%)`
    : 'radial-gradient(70% 50% at 50% 42%, rgba(79,140,255,.18), transparent 72%)';

  return (
    <div className={styles.overlay} onClick={finish} role="presentation">
      <div
        className={styles.glow}
        style={{ background: glowBg, opacity: lifted ? 0 : 1 }}
      />

      {/* round label */}
      <div className={styles.kicker} style={{ opacity: isCount ? 0.9 : 0 }}>
        {roundLabel ?? ''}
      </div>

      {/* stage */}
      <div className={styles.stage}>
        {isCount && (
          <Fragment key={phase}>
            <span
              className={styles.ring}
              style={{ borderColor: COUNT_COLOR[phase as number] }}
            />
            <span
              className={styles.digit}
              style={{
                color: COUNT_COLOR[phase as number],
                textShadow: `0 0 40px ${COUNT_GLOW[phase as number]}`,
              }}
            >
              {phase}
            </span>
          </Fragment>
        )}

        {!isCount && (
          <div className={`${styles.revealWrap}${lifted ? ` ${styles.lifted}` : ''}`}>
            <div className={`${styles.goFlash}${lifted ? ` ${styles.hidden}` : ''}`}>
              Поехали!
            </div>
            <div className={styles.lettersRow}>
              {letters.map((ch, i) => (
                <Fragment key={i}>
                  <div
                    className={styles.tile}
                    style={{
                      color: COLORS[i],
                      borderColor: COLORS[i],
                      boxShadow: `0 0 30px -4px ${COLORS[i]}, inset 0 0 24px -12px ${COLORS[i]}`,
                      animation: `letterReveal .6s cubic-bezier(.2,.9,.3,1) ${i * 0.16}s both`,
                    }}
                  >
                    {ch.toUpperCase()}
                  </div>
                  {i < letters.length - 1 && (
                    <span
                      className={styles.arrow}
                      style={{
                        color: COLORS[i + 1],
                        animationDelay: `${i * 0.16 + 0.2}s`,
                      }}
                    >
                      →
                    </span>
                  )}
                </Fragment>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* sub line */}
      <div className={styles.subline} style={{ opacity: isCount ? 1 : 0 }}>
        Приготовься…
      </div>
    </div>
  );
}
